import { IconCalendarMonth, IconClock } from '@rossum/ui/icons/tabler';
import { IconButton, Stack, SvgIcon, Theme } from '@rossum/ui/material';
import {
  DateField,
  FieldFormatTokenMap,
  PickersActionBar,
  TimePicker,
} from '@rossum/ui/x-date-pickers-pro';
import { applyTextButtonStyle } from './styles';

export const DATE_RANGE_CALENDAR_WIDTH = 624;

const fullDateFormats: Record<string, string> = {
  ja: 'yo MMM do',
  zh: 'yyyy-MM-dd', // TODO: translation format was not checked, not sure it is correct format
  fr: 'dd/MM/yyyy',
  de: 'yyyy-MM-dd', // TODO: translation format was not checked, not sure it is correct format
  es: 'dd/MM/yyyy',
  'pt-br': 'dd/MM/yyyy', // TODO: translation format was not checked, not sure it is correct format
  enUS: 'MM/dd/yyyy',
  en: 'dd/MM/yyyy',
  cs: 'dd.MM.yyyy',
};

const defaultFullDateFormat = 'MM/dd/yyyy';

// MUI Date picker does not work with our preferred japanse format ('yo' for year)
const fallbackFormats: Record<string, string> = {
  ja: 'yyyy MMM do',
};

export const DateTimeInput = ({
  value,
  onChange,
  isActive,
  formatTokenMap,
  hideTime = false,
  locale,
}: {
  value: Date | null;
  onChange: (value: Date | null) => void;
  isActive: boolean;
  formatTokenMap: FieldFormatTokenMap;
  hideTime?: boolean;
  locale: string;
}) => {
  const getSafeFormat = () => {
    const format = fullDateFormats[locale] ?? '';
    // We replicate MUI's way to check for format using `firstWord` to prevent MUI from crashing when it comes accross a format it does not recognise.
    // search for formatToken in https://github.com/mui/mui-x/blob/master/packages/x-date-pickers/src/internals/hooks/useField/useField.utils.ts#L34 for more details
    const firstWord = format.split(' ')[0];
    const formatExists = firstWord ? Boolean(formatTokenMap[firstWord]) : false;

    return formatExists
      ? format
      : (fallbackFormats[locale] ?? defaultFullDateFormat);
  };

  const isActiveStyle = isActive
    ? { fieldset: { borderColor: (t: Theme) => t.palette.secondary.main } }
    : {};

  return (
    <Stack
      direction="row"
      spacing={1}
      px={2}
      width={DATE_RANGE_CALENDAR_WIDTH / 2}
    >
      <DateField
        format={getSafeFormat()}
        value={value}
        onChange={onChange}
        slotProps={{
          textField: {
            inputProps: {
              sx: { pl: 1.5, pr: 1 },
              'data-cy': 'date-time-range-picker-input',
            },
            InputProps: {
              endAdornment: (
                <SvgIcon fontSize="medium" color="disabled">
                  <IconCalendarMonth />
                </SvgIcon>
              ),
              sx: {
                pr: 1.5,
                ...isActiveStyle,
                fontSize: theme => theme.typography.body2.fontSize,
              },
            },
          },
        }}
        sx={{
          flex: 1,
        }}
        size="small"
      />

      {!hideTime && (
        <TimePicker
          value={value}
          onChange={onChange}
          ampm={false}
          slotProps={{
            textField: {
              inputProps: { sx: { pl: 1.5 } },
              InputProps: {
                sx: {
                  pr: 0,
                  ...isActiveStyle,
                  fontSize: theme => theme.typography.body2.fontSize,
                },
              },
              size: 'small',
            },
            layout: {
              sx: {
                ul: {
                  '&:after': { display: 'none' },
                },
                li: {
                  fontSize: theme => theme.typography.body2.fontSize,
                },
              },
            },
            inputAdornment: {
              sx: { ml: 0 },
            },
          }}
          slots={{
            actionBar: props => (
              <PickersActionBar
                {...props}
                sx={{ button: applyTextButtonStyle() }}
              />
            ),
            openPickerButton: props => (
              <Stack pr={1}>
                <IconButton size="small" {...props} sx={{ pr: 0.5 }}>
                  <SvgIcon fontSize="medium" color="disabled">
                    <IconClock />
                  </SvgIcon>
                </IconButton>
              </Stack>
            ),
          }}
          sx={{ flex: 0.5 }}
        />
      )}
    </Stack>
  );
};
